import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { 
  Users, 
  TrendingUp, 
  Activity, 
  Clock,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

export default function EnterpriseDashboard() {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const fetchData = async () => {
    try {
      const orgRes = await apiClient.get(`/organizations/${orgId}`);
      setOrg(orgRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="enterprise-dashboard">
      <div>
        <h1 className="font-heading text-4xl font-bold text-gray-900">
          {org?.name} Dashboard
        </h1>
        <p className="text-lg text-gray-600 mt-2">
          Organization overview and key metrics
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <Card className="p-6 rounded-lg" data-testid="members-stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Members</p>
              <p className="text-3xl font-bold text-gray-900">12</p>
              <p className="text-xs text-success mt-1 flex items-center">
                <TrendingUp className="w-3 h-3 mr-1" />
                +2 this month
              </p>
            </div>
            <div className="p-3 bg-enterprise-slate/10 rounded-lg">
              <Users className="w-6 h-6 text-enterprise-slate" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-lg" data-testid="teams-stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Teams</p>
              <p className="text-3xl font-bold text-gray-900">4</p>
              <p className="text-xs text-gray-500 mt-1">
                Engineering, Sales, Support, HR
              </p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <Activity className="w-6 h-6 text-primary" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-lg" data-testid="usage-stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">AI Usage</p>
              <p className="text-3xl font-bold text-gray-900">2.4k</p>
              <p className="text-xs text-gray-500 mt-1">
                Queries this month
              </p>
            </div>
            <div className="p-3 bg-success/10 rounded-lg">
              <TrendingUp className="w-6 h-6 text-success" />
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-lg" data-testid="plan-stat-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Current Plan</p>
              <p className="text-2xl font-bold text-gray-900 capitalize">{org?.planTier}</p>
              <p className="text-xs text-gray-500 mt-1">
                Upgrade available
              </p>
            </div>
            <div className="p-3 bg-warning/10 rounded-lg">
              <Clock className="w-6 h-6 text-warning" />
            </div>
          </div>
        </Card>

        <Card className="col-span-full md:col-span-2 p-6 rounded-lg" data-testid="getting-started-card">
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Getting Started</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200">
              <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center text-success font-semibold text-sm">
                ✓
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Organization created</p>
                <p className="text-xs text-gray-500">Your workspace is ready</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm">
                2
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Invite team members</p>
                <p className="text-xs text-gray-500">Build your organization</p>
              </div>
              <Button size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" />
                Invite
              </Button>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm">
                3
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Create teams</p>
                <p className="text-xs text-gray-500">Organize by department</p>
              </div>
              <Button size="sm" variant="outline">
                Setup
              </Button>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm">
                4
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Configure SSO</p>
                <p className="text-xs text-gray-500">Secure authentication</p>
              </div>
              <Button size="sm" variant="outline">
                Configure
              </Button>
            </div>
          </div>
        </Card>

        <Card className="col-span-full md:col-span-2 p-6 rounded-lg" data-testid="recent-activity-card">
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex gap-3 pb-3 border-b border-gray-100">
              <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Organization created</p>
                <p className="text-xs text-gray-500">Just now</p>
              </div>
            </div>
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">More activities will appear here</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
